import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import {
  ArrowRight, ChevronRight, FileText, Settings, Lightbulb,
  AlertCircle, CheckCircle, Shield, Zap, BarChart2,
  Upload, Search, Cpu, FileCheck, PackageCheck, Download,
  Building2, Wrench, Factory, ClipboardList,
  Layers, Database, Globe2, Sparkles, Send,
  Briefcase, MessageSquare, Bot, Volume2, VolumeX, Play, Pause, Users,
} from 'lucide-react';
import SiteNavbar from '../components/SiteNavbar';
import AIAssistant from '../components/AIAssistant';
import CustomSolutionModal from '../components/CustomSolutionModal';
import { fetchClients } from '../api/clients';

/* ─── Animated section wrapper ─────────────────────────────────────────── */
function Section({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

/* ─── Badge ─────────────────────────────────────────────────────────────── */
function Badge({ children, variant = 'primary' }) {
  const base = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase';
  const variants = {
    primary: 'bg-primary/10 text-primary border border-primary/20',
    accent:  'bg-accent/15 text-amber-700 border border-accent/30',
    dark:    'bg-white/10 text-white/80 border border-white/20',
  };
  return <span className={`${base} ${variants[variant]}`}>{children}</span>;
}

/* ─── Client Card ───────────────────────────────────────────────────────── */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function ClientCard({ client }) {
  const isIndustrial = client.type === 'industrial';

  const card = (
    <div className="group relative flex flex-col items-center gap-3 px-8 py-5 cursor-default flex-shrink-0 w-40">
      {/* Industry badge */}
      {isIndustrial && (
        <div className="absolute top-2 left-2 flex items-center gap-1 bg-primary/10 rounded-full px-1.5 py-0.5">
          <Building2 size={9} className="text-primary" />
        </div>
      )}

      {/* Logo */}
      <div className="w-20 h-20 flex items-center justify-center overflow-hidden flex-shrink-0">
        <img
          src={`${API_BASE}${client.logoPath}`}
          alt={client.name}
          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110"
          onError={e => { e.target.style.opacity = '0.3'; }}
        />
      </div>

      {/* Name */}
      <p className="text-[11px] font-semibold tracking-wide text-center text-slate-500 group-hover:text-primary transition-colors duration-200 leading-snug">
        {client.name}
      </p>
    </div>
  );

  return client.website ? (
    <a href={client.website} target="_blank" rel="noopener noreferrer" className="block no-underline">
      {card}
    </a>
  ) : card;
}

/* ─── Clients Section (Merged — animated marquee slider) ────────────────── */
function ClientsSection({ lang }) {
  const [clients, setClients] = useState([]);
  const [loaded,  setLoaded]  = useState(false);
  const isAr = lang === 'ar';

  useEffect(() => {
    fetchClients()
      .then(data => {
        console.log('[Clients] fetched:', data);
        const industrial = data.filter(c => c.type === 'industrial');
        const sme        = data.filter(c => c.type === 'sme');
        setClients([...industrial, ...sme]);
        setLoaded(true);
      })
      .catch(err => {
        console.error('[Clients] fetch failed:', err);
        setLoaded(true);
      });
  }, []);

  if (!loaded || clients.length === 0) return null;

  // Triple the list for a seamless infinite loop
  const sliderItems = [...clients, ...clients, ...clients];
  const duration    = Math.max(20, clients.length * 4);

  return (
    <section className="relative bg-white py-16 lg:py-24 overflow-hidden">
      <style>{`
        @keyframes clients-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .clients-marquee-track {
          animation: clients-marquee ${duration}s linear infinite;
          will-change: transform;
        }
        .clients-marquee-track:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Top divider */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

      {/* Header — animates in on mount since section only renders after data loads */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/8 border border-primary/15 mb-4">
            <Users size={11} className="text-primary" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/70">
              {isAr ? 'عملاؤنا' : 'Our Clients'}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black leading-tight text-heading">
            {isAr ? 'موثوق من قِبَل القادة' : 'Trusted by Industry Leaders'}
          </h2>
          <p className="mt-2 text-sm max-w-md mx-auto text-body">
            {isAr
              ? 'نخدم الشركات الصناعية والشركات الصغيرة والمتوسطة في المنطقة'
              : 'Serving industrial enterprises & SMEs across the region'}
          </p>
        </motion.div>
      </div>

      {/* Marquee slider */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="relative"
      >
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-28 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-28 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

        <div className="overflow-hidden">
          <div className="clients-marquee-track flex items-center" style={{ width: 'max-content' }}>
            {sliderItems.map((client, i) => (
              <ClientCard key={`${client._id}-${i}`} client={client} />
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.45 }}
        className="text-center mt-12 text-xs text-slate-400"
      >
        {isAr ? 'انضم إليهم — ' : 'Join them — '}
        <a href="/contact" className="text-primary font-semibold hover:underline underline-offset-2">
          {isAr ? 'ابدأ اليوم' : 'get started today'}
        </a>
      </motion.p>
    </section>
  );
}

/* ─── Video Section ─────────────────────────────────────────────────────── */
function VideoSection({ isRTL, lang }) {
  const videoRef   = useRef(null);
  const sectionRef = useRef(null);
  const inView     = useInView(sectionRef, { once: true, margin: '-100px' });
  const [muted,       setMuted]       = useState(true);
  const [playing,     setPlaying]     = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration,    setDuration]    = useState(0);

  // Auto-play when section enters viewport
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid || !inView) return;
    vid.muted = true;
    vid.play().catch(() => {});
  }, [inView]);

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) { vid.play(); setPlaying(true); }
    else            { vid.pause(); setPlaying(false); }
  };

  const handleSeek = (e) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const vid = videoRef.current;
    if (!vid || !duration) return;
    vid.currentTime = ratio * duration;
    setCurrentTime(vid.currentTime);
  };

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <section ref={sectionRef} className="bg-midnight py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Eyebrow + heading */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <Badge variant="dark"><Sparkles size={12} />{lang === 'ar' ? 'شاهد تولي في العمل' : 'See Tuly in Action'}</Badge>
          <h2 className="mt-4 text-3xl sm:text-4xl font-black text-white leading-tight">
            {lang === 'ar'
              ? 'كيف تعمل منصة تولي؟'
              : 'Watch how Tuly works'}
          </h2>
          <p className="mt-3 text-white/45 text-sm max-w-xl mx-auto leading-relaxed">
            {lang === 'ar'
              ? 'شاهد كيف تُحوّل تولي العمليات اليدوية المعقدة إلى سير عمل آلي في ثوانٍ.'
              : 'See how Tuly transforms complex manual operations into automated workflows in seconds.'}
          </p>
        </motion.div>

        {/* Video container */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="relative mx-auto max-w-4xl group"
        >
          {/* Glow behind the video */}
          <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-primary/40 via-transparent to-accent/20 blur-sm pointer-events-none" />

          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_32px_80px_rgba(0,0,0,0.6)]">
            <video
              ref={videoRef}
              src="/video/intro.mp4"
              loop
              muted
              playsInline
              className="w-full aspect-video object-cover"
              onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
              onLoadedMetadata={e => setDuration(e.target.duration)}
            />

            {/* Controls overlay — visible on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

            <div className="absolute bottom-14 right-4 z-10 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {/* Play / Pause */}
              <button
                onClick={togglePlay}
                className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/25 transition-all"
              >
                {playing
                  ? <Pause size={14} className="text-white" />
                  : <Play  size={14} className="text-white ml-0.5" />}
              </button>
              {/* Mute / Unmute */}
              <button
                onClick={toggleMute}
                className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/25 transition-all"
              >
                {muted
                  ? <VolumeX size={14} className="text-white" />
                  : <Volume2 size={14} className="text-white" />}
              </button>
            </div>

            {/* Muted hint — disappears after first interaction */}
            {muted && (
              <button
                onClick={toggleMute}
                className="absolute bottom-14 left-4 flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1.5 text-white/70 text-xs font-medium hover:bg-white/20 transition-all"
              >
                <VolumeX size={12} />
                {lang === 'ar' ? 'انقر لتشغيل الصوت' : 'Click to unmute'}
              </button>
            )}

            {/* ── Progress / Seek Bar ── */}
            <div className="absolute bottom-0 inset-x-0 z-10 px-4 pb-3 pt-8 bg-gradient-to-t from-black/70 to-transparent">
              {/* Time labels */}
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-white/50 text-[10px] font-mono tabular-nums">{fmtTime(currentTime)}</span>
                <span className="text-white/30 text-[10px] font-mono tabular-nums">{fmtTime(duration)}</span>
              </div>
              {/* Track */}
              <div
                onClick={handleSeek}
                className="relative w-full h-1 bg-white/20 rounded-full cursor-pointer group/seek"
              >
                {/* Buffered / filled */}
                <div
                  className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all duration-100 group-hover/seek:bg-primary/90"
                  style={{ width: `${progress}%` }}
                />
                {/* Thumb */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover/seek:opacity-100 transition-opacity pointer-events-none"
                  style={{ left: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}

/* ─── Workflow Step Colors (light mode) ─────────────────────────────────── */
const WORKFLOW_COLORS = [
  { bg: 'bg-blue-50',     ring: 'ring-blue-200',     text: 'text-blue-600',     ghostHex: '#BFDBFE' },
  { bg: 'bg-sky-50',      ring: 'ring-sky-200',      text: 'text-sky-600',      ghostHex: '#BAE6FD' },
  { bg: 'bg-cyan-50',     ring: 'ring-cyan-200',     text: 'text-cyan-600',     ghostHex: '#A5F3FC' },
  { bg: 'bg-emerald-50',  ring: 'ring-emerald-200',  text: 'text-emerald-600',  ghostHex: '#A7F3D0' },
  { bg: 'bg-amber-50',    ring: 'ring-amber-200',    text: 'text-amber-600',    ghostHex: '#FDE68A' },
  { bg: 'bg-yellow-50',   ring: 'ring-yellow-200',   text: 'text-yellow-600',   ghostHex: '#FEF08A' },
];

/* ─── Workflow Step Card (light redesign) ───────────────────────────────── */
function WorkflowStep({ icon: Icon, label, desc, index, inView }) {
  const c = WORKFLOW_COLORS[index];
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.12 + 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="flex flex-col items-center text-center"
    >
      {/* Icon — connector line passes through center (top-7 from grid top) */}
      <motion.div
        initial={{ scale: 0, rotate: -10 }}
        animate={inView ? { scale: 1, rotate: 0 } : {}}
        transition={{ duration: 0.5, delay: index * 0.1 + 0.15, type: 'spring', stiffness: 220, damping: 14 }}
        whileHover={{ scale: 1.13, y: -4 }}
        className={`relative z-10 w-14 h-14 rounded-2xl ${c.bg} ring-2 ${c.ring} flex items-center justify-center shadow-sm cursor-default`}
      >
        <Icon size={22} className={c.text} />
      </motion.div>

      {/* Ghost "0N" number */}
      <div className="flex justify-center items-end mt-3 mb-2 pointer-events-none select-none" aria-hidden>
        <span className="text-[3rem] sm:text-[4.5rem] font-black leading-none" style={{ color: c.ghostHex }}>0</span>
        <span className="text-[2.1rem] sm:text-[3.2rem] font-black leading-none -ml-1.5 sm:-ml-2 mb-0.5" style={{ color: c.ghostHex }}>
          {index + 1}
        </span>
      </div>

      {/* Step number + label + desc */}
      <span className={`block text-[10px] font-bold tracking-[0.2em] uppercase ${c.text} mb-1.5`}>
        {String(index + 1).padStart(2, '0')}
      </span>
      <p className="text-gray-900 font-bold text-sm mb-1.5 leading-snug">{label}</p>
      <p className="text-gray-400 text-[11px] leading-relaxed max-w-[84px]">{desc}</p>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LANDING PAGE — HOMEPAGE
═══════════════════════════════════════════════════════════════════════════ */
export default function Landing() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';

  const [customModalOpen, setCustomModalOpen] = useState(false);

  const heroRef = useRef(null);
  const workflowRef = useRef(null);
  const workflowInView = useInView(workflowRef, { once: true, margin: '-80px' });
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const workflowSteps = [
    { icon: Upload,    key: 'step1' },
    { icon: Search,    key: 'step2' },
    { icon: Cpu,       key: 'step3' },
    { icon: FileCheck, key: 'step4' },
    { icon: Shield,    key: 'step5' },
    { icon: Download,  key: 'step6' },
  ];

  const problems = [
    { icon: AlertCircle, titleKey: 'p1Title', descKey: 'p1Desc', color: 'text-error',   bg: 'bg-red-50' },
    { icon: Zap,         titleKey: 'p2Title', descKey: 'p2Desc', color: 'text-warning', bg: 'bg-amber-50' },
    { icon: FileText,    titleKey: 'p3Title', descKey: 'p3Desc', color: 'text-info',    bg: 'bg-sky-50' },
    { icon: BarChart2,   titleKey: 'p4Title', descKey: 'p4Desc', color: 'text-muted',   bg: 'bg-white' },
  ];

  const outputs = [
    { icon: Database,     titleKey: 'o1Title', descKey: 'o1Desc', color: 'text-primary', bg: 'bg-primary/8' },
    { icon: AlertCircle,  titleKey: 'o2Title', descKey: 'o2Desc', color: 'text-warning', bg: 'bg-amber-50' },
    { icon: CheckCircle,  titleKey: 'o3Title', descKey: 'o3Desc', color: 'text-success', bg: 'bg-emerald-50' },
    { icon: PackageCheck, titleKey: 'o4Title', descKey: 'o4Desc', color: 'text-accent',  bg: 'bg-accent/8' },
  ];

  const industries = [
    { icon: Building2,     titleKey: 'hvacTitle',          descKey: 'hvacDesc',          img: '/industrial/diffusers.webp',                href: '/industries/hvac' },
    { icon: Wrench,        titleKey: 'mepTitle',           descKey: 'mepDesc',           img: '/industrial/engineering drawings 1.avif',   href: '/industries/mep' },
    { icon: Factory,       titleKey: 'manufacturingTitle', descKey: 'manufacturingDesc', img: '/industrial/industrial ducting system.jpg', href: '/industries/manufacturing' },
    { icon: ClipboardList, titleKey: 'tenderTitle',        descKey: 'tenderDesc',        img: '/industrial/engineering drawings 2.jpg',    href: '/industries/tender' },
  ];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <SiteNavbar />
      <CustomSolutionModal isOpen={customModalOpen} onClose={() => setCustomModalOpen(false)} />
      <AIAssistant source="landing" />

      {/* ══════════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center overflow-hidden bg-midnight -mt-16 pt-16">
        {/* Blueprint grid */}
        <div className="absolute inset-0 blueprint-grid-dark opacity-50" />

        {/* Gradient orbs */}
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-primary/15 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/3 w-[300px] h-[300px] rounded-full bg-accent/8 blur-[80px] pointer-events-none" />

        {/* Industrial image — right side */}
        <div className={`absolute inset-y-0 ${isRTL ? 'left-0' : 'right-0'} w-1/2 hidden lg:block pointer-events-none`}>
          <img
            src="/industrial/mechanical rooms.jpg"
            alt="Industrial facility"
            className="w-full h-full object-cover opacity-50"
          />
          <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-transparent to-midnight' : 'from-midnight to-transparent'}`} />
        </div>

        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24"
        >
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge variant="dark"><Sparkles size={12} />{t('hero.badge')}</Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-[3.5rem] font-black text-white leading-[1.06] tracking-tight"
            >
              {t('hero.headline1')}{' '}
              <span className="gradient-text">{t('hero.headline2')}</span>{' '}
              {t('hero.headline3')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed max-w-xl"
            >
              {t('hero.subheadline')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <button
                onClick={() => navigate('/contact')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
              >
                {t('hero.ctaDemo')}
                <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
              </button>
              <button
                onClick={() => navigate('/solutions')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/8 text-white font-semibold text-sm hover:bg-white/14 transition-all border border-white/20"
              >
                {t('hero.ctaSolutions')}
              </button>
              <button
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-accent/15 text-accent font-semibold text-sm hover:bg-accent/22 transition-all border border-accent/30"
              >
                <Sparkles size={15} />{t('hero.ctaAssistant')}
              </button>
            </motion.div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-12 flex flex-wrap gap-10"
            >
              {[
                [t('hero.stat1Value'), t('hero.stat1Label')],
                [t('hero.stat2Value'), t('hero.stat2Label')],
                [t('hero.stat3Value'), t('hero.stat3Label')],
              ].map(([val, label], i) => (
                <div key={i}>
                  <p className="text-3xl font-black gradient-text">{val}</p>
                  <p className="text-white/45 text-xs mt-0.5">{label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5"
        >
          <span className="text-white/25 text-[10px] tracking-widest uppercase">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="w-px h-8 bg-gradient-to-b from-white/25 to-transparent"
          />
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════════════
          VIDEO SECTION
      ══════════════════════════════════════════════════════ */}
      <VideoSection isRTL={isRTL} lang={i18n.language} />

      {/* ══════════════════════════════════════════════════════
          CLIENTS SECTION
      ══════════════════════════════════════════════════════ */}
      <ClientsSection lang={i18n.language} />

      {/* ══════════════════════════════════════════════════════
          PROBLEM SECTION
      ══════════════════════════════════════════════════════ */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><AlertCircle size={12} />{t('problems.badge')}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">{t('problems.headline')}</h2>
            <p className="mt-3 text-body max-w-2xl mx-auto text-sm leading-relaxed">{t('problems.subheadline')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {problems.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-border-light card-hover"
              >
                <div className={`w-10 h-10 rounded-xl ${p.bg} flex items-center justify-center mb-4`}>
                  <p.icon size={18} className={p.color} />
                </div>
                <h3 className="text-heading font-bold text-sm mb-2">{t(`problems.${p.titleKey}`)}</h3>
                <p className="text-body text-xs leading-relaxed">{t(`problems.${p.descKey}`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════
          SOLUTIONS — 3 CARDS
      ══════════════════════════════════════════════════════ */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><Layers size={12} />{t('solutions.badge')}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">{t('solutions.headline')}</h2>
            <p className="mt-3 text-body text-sm">{t('solutions.subheadline')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* BoQ Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              onClick={() => navigate('/solutions/boq')}
              className="relative rounded-2xl overflow-hidden border border-border-light bg-white card-hover group cursor-pointer"
            >
              <div className="h-1 bg-primary-gradient" />
              <div className="p-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <FileText size={22} className="text-primary" />
                </div>
                <h3 className="text-heading font-bold text-xl mb-3">{t('solutions.boqTitle')}</h3>
                <p className="text-body text-sm leading-relaxed mb-5">{t('solutions.boqDesc')}</p>
                <ul className="space-y-2 mb-6">
                  {['boqFeature1', 'boqFeature2', 'boqFeature3'].map((k) => (
                    <li key={k} className="flex items-start gap-2 text-xs text-body">
                      <CheckCircle size={13} className="text-success mt-0.5 flex-shrink-0" />
                      {t(`solutions.${k}`)}
                    </li>
                  ))}
                </ul>
                <div className="rounded-xl overflow-hidden border border-border-light">
                  <img src="/mockup/boq/screen 1.png" alt="BoQ" className="w-full group-hover:scale-[1.03] transition-transform duration-500" />
                </div>
                <div className="mt-5 flex items-center gap-1.5 text-primary text-sm font-semibold">
                  {t('solutions.learnMore')}
                  <ArrowRight size={15} className={`group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </motion.div>

            {/* Submittals Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              onClick={() => navigate('/solutions/submittals')}
              className="relative rounded-2xl overflow-hidden border border-border-light bg-white card-hover group cursor-pointer"
            >
              <div className="h-1 bg-gradient-to-r from-success to-info" />
              <div className="p-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-4">
                  <Settings size={22} className="text-success" />
                </div>
                <h3 className="text-heading font-bold text-xl mb-3">{t('solutions.submittalsTitle')}</h3>
                <p className="text-body text-sm leading-relaxed mb-5">{t('solutions.submittalsDesc')}</p>
                <ul className="space-y-2 mb-6">
                  {['submittalsFeature1', 'submittalsFeature2', 'submittalsFeature3'].map((k) => (
                    <li key={k} className="flex items-start gap-2 text-xs text-body">
                      <CheckCircle size={13} className="text-success mt-0.5 flex-shrink-0" />
                      {t(`solutions.${k}`)}
                    </li>
                  ))}
                </ul>
                <div className="rounded-xl overflow-hidden border border-border-light">
                  <img src="/mockup/ts/dashboard_redesign.png" alt="Submittals" className="w-full group-hover:scale-[1.03] transition-transform duration-500" />
                </div>
                <div className="mt-5 flex items-center gap-1.5 text-success text-sm font-semibold">
                  {t('solutions.learnMore')}
                  <ArrowRight size={15} className={`group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </motion.div>

            {/* Custom Solution Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              onClick={() => setCustomModalOpen(true)}
              className="relative rounded-2xl overflow-hidden border border-accent/25 bg-gradient-to-br from-[#FFFCF0] to-white card-hover group cursor-pointer"
            >
              <div className="h-1 bg-accent-gradient" />
              <div className="p-6">
                <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center mb-4">
                  <Lightbulb size={22} className="text-accent" />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-heading font-bold text-xl">{t('solutions.customTitle')}</h3>
                  <span className="px-2 py-0.5 rounded-full bg-accent/20 text-amber-700 text-xs font-semibold">
                    {i18n.language === 'ar' ? 'مخصص' : 'Custom'}
                  </span>
                </div>
                <p className="text-body text-sm leading-relaxed mb-5">{t('solutions.customDesc')}</p>
                <ul className="space-y-2 mb-6">
                  {['customFeature1', 'customFeature2', 'customFeature3'].map((k) => (
                    <li key={k} className="flex items-start gap-2 text-xs text-body">
                      <CheckCircle size={13} className="text-accent mt-0.5 flex-shrink-0" />
                      {t(`solutions.${k}`)}
                    </li>
                  ))}
                </ul>

                {/* Animated orbs illustration */}
                <div className="rounded-xl bg-accent/8 border border-accent/15 p-8 flex flex-col items-center text-center">
                  <div className="flex gap-3 mb-4">
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        animate={{ y: [0, -8, 0] }}
                        transition={{ duration: 1.6, delay: i * 0.3, repeat: Infinity }}
                        className="w-4 h-4 rounded-full bg-accent/50"
                      />
                    ))}
                  </div>
                  <p className="text-amber-700 text-sm font-medium">
                    {i18n.language === 'ar' ? 'صف تحديك' : 'Describe your challenge'}
                  </p>
                  <p className="text-amber-600/60 text-xs mt-1">
                    {i18n.language === 'ar' ? 'وسنهندس لك الحل' : "We'll engineer the solution"}
                  </p>
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); setCustomModalOpen(true); }}
                  className="mt-5 w-full py-3 rounded-xl bg-accent text-[#1A1A1A] font-semibold text-sm hover:bg-accent-hover transition-colors flex items-center justify-center gap-2"
                >
                  {t('solutions.customCta')}
                  <ChevronRight size={15} className={isRTL ? 'rotate-180' : ''} />
                </button>
              </div>
            </motion.div>
          </div>

          <div className="mt-10 text-center">
            <button
              onClick={() => navigate('/solutions')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-primary/20 text-primary font-semibold text-sm hover:bg-primary/5 transition-all"
            >
              {t('solutions.exploreSolutions')}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </button>
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════════════════ */}
      <Section className="py-24 bg-white relative overflow-hidden">
        {/* Subtle grid texture */}
        <div className="absolute inset-0 blueprint-grid opacity-[0.35]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* ── Header ── */}
          <div className="text-center mb-16">
            <Badge variant="primary"><Zap size={12} />{t('workflow.badge')}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading leading-tight">
              {t('workflow.headline')}
            </h2>
            <p className="mt-3 text-body text-sm max-w-lg mx-auto leading-relaxed">
              {i18n.language === 'ar'
                ? 'ستة خطوات محكومة تحوّل مستنداتك الخام إلى مخرجات رقمية جاهزة للتسليم.'
                : 'Six governed steps that transform raw documents into controlled, ERP-ready digital outputs.'}
            </p>
          </div>

          {/* ── Pipeline ── */}
          <div ref={workflowRef} className="relative">

            {/* Gradient connector line — desktop only, aligned with icon centers (top-7 = 28px) */}
            <div className="hidden lg:block absolute top-7 left-[8%] right-[8%] pointer-events-none" style={{ transform: 'translateY(-50%)' }}>
              {/* Base gradient track */}
              <div className="h-0.5 rounded-full bg-gradient-to-r from-blue-200 via-cyan-200 via-emerald-200 to-amber-200 opacity-80" />

              {/* Primary glowing particle */}
              <motion.div
                animate={workflowInView
                  ? { left: ['-20%', '120%'], opacity: [0, 1, 1, 0] }
                  : { left: '-20%', opacity: 0 }}
                transition={{ duration: 3.5, delay: 1.0, repeat: Infinity, repeatDelay: 2.5, ease: 'easeInOut' }}
                style={{ position: 'absolute', top: '-4px', width: '18%' }}
                className="h-[9px] bg-gradient-to-r from-transparent via-blue-400 to-transparent blur-[3px] rounded-full"
              />
              {/* Secondary particle — offset, amber-toned */}
              <motion.div
                animate={workflowInView
                  ? { left: ['-14%', '114%'], opacity: [0, 0.7, 0.7, 0] }
                  : { left: '-14%', opacity: 0 }}
                transition={{ duration: 2.8, delay: 2.8, repeat: Infinity, repeatDelay: 3.2, ease: 'easeInOut' }}
                style={{ position: 'absolute', top: '-3px', width: '12%' }}
                className="h-[7px] bg-gradient-to-r from-transparent via-amber-400 to-transparent blur-[2px] rounded-full"
              />
            </div>

            {/* Step cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-y-10 gap-x-3 lg:gap-x-2">
              {workflowSteps.map((step, i) => (
                <WorkflowStep
                  key={step.key}
                  icon={step.icon}
                  label={t(`workflow.${step.key}`)}
                  desc={t(`workflow.${step.key}Desc`)}
                  index={i}
                  inView={workflowInView}
                />
              ))}
            </div>
          </div>

          {/* ── What You Get ── */}
          <div className="mt-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="flex-1 h-px bg-gray-100" />
              <span className="text-gray-400 text-[10px] font-bold uppercase tracking-[0.25em] flex-shrink-0">
                {i18n.language === 'ar' ? 'ما الذي ستحصل عليه' : 'What you get'}
              </span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: FileText,     label: i18n.language === 'ar' ? 'قوائم الكميات'   : 'BoQ Files',    sub: 'Excel · PDF · Word',   border: 'border-blue-100',    bg: 'bg-blue-50',     ic: 'text-blue-500',    ibg: 'bg-blue-100'     },
                { icon: Database,     label: i18n.language === 'ar' ? 'بيانات الكتالوج'  : 'Catalog Data', sub: 'Models & rules',        border: 'border-cyan-100',    bg: 'bg-cyan-50',     ic: 'text-cyan-600',    ibg: 'bg-cyan-100'     },
                { icon: Layers,       label: i18n.language === 'ar' ? 'مخرجات ERP'      : 'ERP Output',   sub: 'Odoo · SAP · Oracle',  border: 'border-emerald-100', bg: 'bg-emerald-50',  ic: 'text-emerald-600', ibg: 'bg-emerald-100'  },
                { icon: PackageCheck, label: i18n.language === 'ar' ? 'حزم التوصيف'     : 'Submittals',   sub: 'Word · PDF',            border: 'border-amber-100',   bg: 'bg-amber-50',    ic: 'text-amber-600',   ibg: 'bg-amber-100'    },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.09 + 0.1 }}
                  className={`flex items-center gap-4 rounded-2xl border ${item.border} ${item.bg} px-5 py-4`}
                >
                  <div className={`w-10 h-10 rounded-xl ${item.ibg} flex items-center justify-center flex-shrink-0`}>
                    <item.icon size={18} className={item.ic} />
                  </div>
                  <div>
                    <p className="text-gray-900 font-semibold text-sm leading-snug">{item.label}</p>
                    <p className="text-gray-400 text-[11px] mt-0.5">{item.sub}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════
          OUTPUTS
      ══════════════════════════════════════════════════════ */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><PackageCheck size={12} />{t('outputs.badge')}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">{t('outputs.headline')}</h2>
            <p className="mt-3 text-body text-sm">{t('outputs.subheadline')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {outputs.map((o, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-border-light card-hover"
              >
                <div className={`w-10 h-10 rounded-xl ${o.bg} flex items-center justify-center mb-4`}>
                  <o.icon size={18} className={o.color} />
                </div>
                <h3 className="text-heading font-bold text-sm mb-2">{t(`outputs.${o.titleKey}`)}</h3>
                <p className="text-body text-xs leading-relaxed">{t(`outputs.${o.descKey}`)}</p>
              </motion.div>
            ))}
          </div>

        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════
          INDUSTRIES
      ══════════════════════════════════════════════════════ */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><Globe2 size={12} />{t('industries.badge')}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">{t('industries.headline')}</h2>
            <p className="mt-3 text-body text-sm max-w-xl mx-auto">{t('industries.subheadline')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {industries.map((ind, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                onClick={() => navigate(ind.href)}
                className="relative rounded-2xl overflow-hidden cursor-pointer group"
              >
                <div className="aspect-[4/3] relative">
                  <img
                    src={ind.img}
                    alt={t(`industries.${ind.titleKey}`)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-5 h-5 rounded-md bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <ind.icon size={11} className="text-accent" />
                    </div>
                    <h3 className="text-white font-bold text-sm drop-shadow-sm">{t(`industries.${ind.titleKey}`)}</h3>
                  </div>
                  <p className="text-white/80 text-xs leading-snug line-clamp-2 drop-shadow-sm">{t(`industries.${ind.descKey}`)}</p>
                  <div className="mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/10 backdrop-blur-sm text-white text-xs font-semibold group-hover:bg-primary group-hover:text-white transition-all">
                    {i18n.language === 'ar' ? 'اعرف المزيد' : 'Learn more'}
                    <ArrowRight size={11} className={`group-hover:translate-x-0.5 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <button
              onClick={() => navigate('/industries')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-primary/20 text-primary font-semibold text-sm hover:bg-primary/5 transition-all"
            >
              {t('industries.exploreIndustries')}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </button>
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════
          SME SOLUTIONS
      ══════════════════════════════════════════════════════ */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="accent"><Briefcase size={12} />{t('sme.badge')}</Badge>
              <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">{t('sme.headline')}</h2>
              <p className="mt-3 text-body text-sm leading-relaxed">{t('sme.subheadline')}</p>
              <div className="mt-8 space-y-4">
                {[
                  { title: t('sme.erp'),      desc: t('sme.erpDesc'),      icon: Database,      color: 'text-primary', bg: 'bg-primary/8' },
                  { title: t('sme.crm'),      desc: t('sme.crmDesc'),      icon: MessageSquare, color: 'text-success', bg: 'bg-emerald-50' },
                  { title: t('sme.workflow'), desc: t('sme.workflowDesc'), icon: Layers,        color: 'text-accent',  bg: 'bg-accent/8' },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-start gap-4 p-4 rounded-xl border border-border-light hover:border-primary/20 hover:bg-white transition-all"
                  >
                    <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center flex-shrink-0`}>
                      <item.icon size={18} className={item.color} />
                    </div>
                    <div>
                      <h3 className="text-heading font-semibold text-sm">{item.title}</h3>
                      <p className="text-body text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              <button
                onClick={() => navigate('/sme-solutions')}
                className="mt-6 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary/8 text-primary font-semibold text-sm hover:bg-primary/14 transition-all border border-primary/20"
              >
                {t('sme.learnMore')}
                <ArrowRight size={15} className={isRTL ? 'rotate-180' : ''} />
              </button>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-card-hover">
                <img src="/industrial/engineering drawings 4.jpg" alt="SME Solutions" className="w-full object-cover" />
              </div>
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className={`absolute -bottom-5 ${isRTL ? 'left-5' : 'right-5'} bg-white rounded-xl shadow-card-hover p-4 border border-border-light`}
              >
                <p className="text-heading font-bold text-sm">{i18n.language === 'ar' ? 'جاهز للتكامل' : 'Integration Ready'}</p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {['Odoo', 'SAP', 'Oracle'].map((p) => (
                    <span key={p} className="px-2 py-0.5 rounded-full bg-primary/8 text-primary text-xs font-medium">{p}</span>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════════════════════ */}
      <Section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-50" />
        <div className="absolute top-0 right-1/3 w-72 h-72 rounded-full bg-primary/5 blur-[80px]" />
        <div className="absolute bottom-0 left-1/3 w-56 h-56 rounded-full bg-accent/4 blur-[60px]" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-black text-heading">{t('cta.headline')}</h2>
          <p className="mt-4 text-body leading-relaxed text-sm max-w-xl mx-auto">{t('cta.subheadline')}</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
            >
              {t('cta.bookDemo')}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl border-2 border-border-light text-body font-semibold hover:border-primary/30 hover:text-primary transition-all"
            >
              {t('cta.contactUs')}
            </button>
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════
          FOOTER
      ══════════════════════════════════════════════════════ */}
      <footer className="bg-midnight border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/tuly.png" alt="Tuly Technology" className="h-8 w-auto" />
                <span className="text-white font-bold text-sm">Tuly Technology</span>
              </div>
              <p className="text-muted text-xs leading-relaxed">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.solutions')}</h4>
              <ul className="space-y-2.5">
                {[['boq', '/solutions/boq'], ['submittals', '/solutions/submittals'], ['platform', '/platform']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.company')}</h4>
              <ul className="space-y-2.5">
                {[['about', '/about'], ['contact', '/contact']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.legal')}</h4>
              <ul className="space-y-2.5">
                {[['privacy', '/privacy'], ['terms', '/terms']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-dark-border flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-muted text-xs">© {new Date().getFullYear()} Tuly Technology. {t('footer.allRights')}</p>
            <p className="text-muted/50 text-xs">{t('footer.madeWith')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
